import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { StudentNav } from '../../../common/student-nav/student-nav';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { CareerPageService } from '../../../../services/career-page.service';
import { Career } from '../../../../models/career';
import { CareerType } from '../../../../models/careerType';

interface CareerSummary {
  id: string;
  title: string;
  tagline: string;
  category: string;
  emoji: string;
  avgSalary: string;
}

/** Displays the student-facing career exploration list. */
@Component({
  selector: 'app-careers',
  standalone: true,
  imports: [
    CommonModule,
    StudentNav,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    FormsModule,
  ],
  templateUrl: './careers.html',
  styleUrl: './careers.css',
})
export class Careers implements OnInit {
  cdf = inject(ChangeDetectorRef);
  router = inject(Router);
  route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  careerService = inject(CareerPageService);
  careerTypes = signal<CareerType[]>([]);
  searchQuery = '';
  /** Currently active filter chip value. */
  activeFilter = 'All';
  selectedCareer: Career | null = null;
  /** Career entries displayed in the list, populated from mock data on init. */
  careers = signal<Career[]>([]);

  /**
   * List of filters including status and carrer types
   * @returns {string[]}
   */
  get filters() {
    return ['All', ...this.careerTypes().map((ct) => ct.name)];
  }

  /**
   * Get list of filtered careers
   * @returns {Career[]}
   */
  get filteredCareers() {
    let careers = this.careers();

    if (this.activeFilter && this.activeFilter !== 'All') {
      const type = this.careerTypes().find((ct) => ct.name === this.activeFilter);
      careers = careers.filter((c) => c.career_type_id === type?.id);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase();
      careers = careers.filter((c) => c.name.toLowerCase().includes(q));
    }

    return careers;
  }

  ngOnInit() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const q = params.get('q') ?? '';
      if (q !== this.searchQuery) {
        this.searchQuery = q;
        this.cdf.detectChanges();
      }
    });

    this.getCareers();
    this.getCareerTypes();
  }

  /**
   * Updates the search box and mirrors it into the URL's `q` param, so the
   * global search results page can hand off here with a query and this page
   * stays linkable/bookmarkable while typing.
   * @param {string} value - The new search box value.
   * @returns {void}
   */
  onSearchInput(value: string): void {
    this.searchQuery = value;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: value || null },
      replaceUrl: true,
    });
  }

  getCareers() {
    this.careerService.getCareers().subscribe({
      next: (careers) => {
        this.careers.set(careers);
      },
    });
  }

  getCareerTypes() {
    this.careerService.getTypes().subscribe({
      next: (types) => {
        this.careerTypes.set(types);
      },
    });
  }

  openPage(career: Career) {
    this.router.navigate(['/counselors/careers', career.id]);
  }
}
